import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Thermometer, Snowflake, Play, Square, RefreshCw,
  AlertTriangle, CheckCircle, Send, Inbox,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TemperatureMonitoringCard
 *
 * Scoped to a single batch (the batch currently open on the Batch Detail
 * page). Lets the operator:
 *   • Start a monitoring session for THIS batch
 *   • Send a manual temperature reading (°C) — quick-send always available
 *   • View latest reading, timestamp, and min / max / avg
 *   • End the active session
 *
 * Backend contract (already exists — no schema changes):
 *   POST /api/monitoring-sessions
 *   POST /api/monitoring-sessions/{session_id}/end
 *   POST /api/sensors/temperature
 *   GET  /api/batches/{batch_id}/temperature
 *
 * Existing batch records, NFC bindings, audit trail, and chain-of-custody
 * are NOT modified by this component.
 */
const TemperatureMonitoringCard = ({ batch, canManage }) => {
  const batchId = batch?.id;
  const [readings, setReadings] = useState([]);
  const [session, setSession] = useState(null);   // current active session for this batch
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tempInput, setTempInput] = useState('');
  const [sensorIdInput, setSensorIdInput] = useState('');

  const authConfig = () => {
    const token = localStorage.getItem('mvp_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  /* ---------------- Data loading ---------------- */
  const refresh = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      // Pull this batch's temperature history (existing endpoint).
      const r = await axios
        .get(`${BACKEND_URL}/api/batches/${encodeURIComponent(batchId)}/temperature`)
        .catch(() => ({ data: [] }));
      setReadings(Array.isArray(r.data) ? r.data : []);

      // Pull active sessions and find the one bound to this batch (if any).
      const s = await axios
        .get(`${BACKEND_URL}/api/monitoring-sessions/active`)
        .catch(() => ({ data: [] }));
      const list = Array.isArray(s.data) ? s.data : [];
      const mine = list.find((it) => it.batch_id === batchId) || null;
      setSession(mine);
      if (mine?.sensor_id) setSensorIdInput((prev) => prev || mine.sensor_id);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { refresh(); }, [refresh]);

  /* ---------------- Derived stats ---------------- */
  const stats = useMemo(() => {
    if (!readings.length) return null;
    const temps = readings
      .map((r) => Number(r.temperature_celsius))
      .filter((n) => !Number.isNaN(n));
    if (!temps.length) return null;
    const min = Math.min(...temps);
    const max = Math.max(...temps);
    const avg = temps.reduce((a, b) => a + b, 0) / temps.length;
    // Latest reading: backend returns sorted; if not, sort defensively desc by timestamp.
    const sorted = [...readings].sort((a, b) => {
      const ta = new Date(a.timestamp || a.created_at || 0).getTime();
      const tb = new Date(b.timestamp || b.created_at || 0).getTime();
      return tb - ta;
    });
    return { min, max, avg, latest: sorted[0], count: temps.length };
  }, [readings]);

  /* ---------------- Actions ---------------- */
  const startSession = async () => {
    if (busy || !batchId) return;
    const sensorId = (sensorIdInput || 'DS18B20-01').trim() || 'DS18B20-01';
    setBusy(true);
    try {
      // ---- Frontend safety net: warn loudly if this sensor is already
      // bound to a different batch. Backend (server.py) already scopes the
      // session lookup by sensor_id + batch_id, so even if the operator
      // proceeds the readings still bind to THIS batch. The warning is
      // purely an operator-experience aid — it prevents accidental dual
      // monitoring on the same physical probe.
      try {
        const { data: active } = await axios.get(
          `${BACKEND_URL}/api/monitoring-sessions/active`, authConfig(),
        );
        const list = Array.isArray(active) ? active : [];
        const conflict = list.find(
          (it) => it.sensor_id === sensorId && it.batch_id && it.batch_id !== batchId,
        );
        if (conflict) {
          toast.warning(
            `Sensor "${sensorId}" is already active on another batch ` +
            `(${(conflict.product_name || conflict.batch_id || '').slice(0, 24)}). ` +
            `Readings will still attach to this batch correctly, but consider ` +
            `ending the other session or using a unique sensor id.`,
            { duration: 8000 },
          );
        }
      } catch { /* non-fatal */ }

      const body = {
        batch_id: batchId,
        product_name: batch?.product_name || '',
        product_type: batch?.product_type || batch?.species || '',
        species_or_category: batch?.species || batch?.species_or_category || '',
        supplier: batch?.supplier || '',
        monitoring_session_name: `Batch ${batchId.slice(0, 8)} · ${new Date()
          .toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`,
        sensor_id: sensorId,
      };
      const { data } = await axios.post(
        `${BACKEND_URL}/api/monitoring-sessions`, body, authConfig(),
      );
      setSession(data);
      toast.success('Monitoring session started for this batch.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start monitoring session.');
    } finally {
      setBusy(false);
    }
  };

  const endSession = async () => {
    if (busy || !session?.id) return;
    const ok = window.confirm(
      'End the active monitoring session for this batch?\n\nAll readings and audit trail will be preserved.',
    );
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/monitoring-sessions/${encodeURIComponent(session.id)}/end`,
        {}, authConfig(),
      );
      setSession(null);
      toast.success('Session ended. History preserved.');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not end session.');
    } finally {
      setBusy(false);
    }
  };

  const sendReading = async () => {
    if (busy || !batchId) return;
    const value = parseFloat(tempInput);
    if (Number.isNaN(value)) {
      toast.error('Enter a valid temperature in °C (e.g. 4.2).');
      return;
    }
    const sensorId = (sensorIdInput || session?.sensor_id || 'DS18B20-01').trim();
    setBusy(true);
    try {
      // The backend auto-enriches the reading with the active session's batch_id
      // when a session exists. If none exists, we explicitly attach this batch's id.
      const body = {
        sensor_id: sensorId,
        temperature_celsius: value,
        batch_id: batchId,
        product_name: batch?.product_name || undefined,
        product_type: batch?.product_type || undefined,
        species_or_category: batch?.species || batch?.species_or_category || undefined,
      };
      const { data } = await axios.post(
        `${BACKEND_URL}/api/sensors/temperature`, body, authConfig(),
      );
      setReadings((prev) => [data, ...prev]);
      setTempInput('');
      const statusLabel = data?.status ? ` · ${data.status.toUpperCase()}` : '';
      toast.success(`Reading recorded: ${value} °C${statusLabel}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record reading.');
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- Render ---------------- */
  if (!batchId) return null;

  return (
    <section
      data-testid="temperature-monitoring-card"
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
            <Thermometer className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <h2
              className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800"
              style={{ fontFamily: 'Manrope' }}
              data-testid="temperature-monitoring-title"
            >
              Temperature & Cold-Chain
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
              Manage the monitoring session and record temperature readings for this batch.
            </p>
          </div>
        </div>
        <SessionStatusBadge session={session} />
      </div>

      {/* Session control row */}
      <div
        className="rounded-lg border border-slate-200 p-3 sm:p-4 bg-slate-50"
        data-testid="temperature-session-block"
      >
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 min-w-0">
            <label
              htmlFor="temp-sensor-id"
              className="block text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 mb-1"
            >
              Sensor ID
            </label>
            <input
              id="temp-sensor-id"
              type="text"
              data-testid="temperature-sensor-id-input"
              value={sensorIdInput}
              onChange={(e) => setSensorIdInput(e.target.value)}
              placeholder="DS18B20-01"
              disabled={!!session || busy}
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:bg-slate-100 disabled:text-slate-500"
            />
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {session ? (
              <Button
                data-testid="temperature-end-session"
                onClick={endSession}
                disabled={busy || !canManage}
                className="bg-slate-800 hover:bg-slate-900 text-white h-10"
              >
                <Square className="w-4 h-4 mr-1.5" />
                End session
              </Button>
            ) : (
              <Button
                data-testid="temperature-start-session"
                onClick={startSession}
                disabled={busy || !canManage}
                className="bg-emerald-700 hover:bg-emerald-800 text-white h-10"
              >
                <Play className="w-4 h-4 mr-1.5" />
                Start session
              </Button>
            )}
            <Button
              data-testid="temperature-refresh"
              onClick={refresh}
              variant="outline"
              disabled={busy || loading}
              className="h-10"
              title="Reload readings"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {!canManage && (
          <p className="text-[11px] sm:text-xs text-slate-500 mt-2">
            Your role can view readings but not manage sessions for this batch.
          </p>
        )}
      </div>

      {/* Quick-send reading row (always available) */}
      <div
        className="mt-3 rounded-lg border border-slate-200 p-3 sm:p-4 bg-white"
        data-testid="temperature-quicksend-block"
      >
        <p className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-500 mb-1.5">
          Send reading
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="number"
              step="0.1"
              data-testid="temperature-value-input"
              value={tempInput}
              onChange={(e) => setTempInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendReading(); }}
              placeholder="e.g. 4.2"
              disabled={busy || !canManage}
              className="w-full bg-white border border-slate-300 rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:bg-slate-100 disabled:text-slate-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-slate-500">
              °C
            </span>
          </div>
          <Button
            data-testid="temperature-send-reading"
            onClick={sendReading}
            disabled={busy || !canManage || tempInput.trim() === ''}
            className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 sm:w-auto"
          >
            <Send className="w-4 h-4 mr-1.5" />
            Send
          </Button>
        </div>
        <p className="text-[11px] sm:text-xs text-slate-500 mt-2">
          Thresholds: &lt; 4 °C normal · 4 – 6 °C warning · ≥ 6 °C critical.
          {session ? ' Reading attaches to the active session.' : ' Reading attaches directly to this batch.'}
        </p>
      </div>

      {/* Latest + stats row */}
      <div
        className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3"
        data-testid="temperature-stats-block"
      >
        <StatTile
          label="Latest"
          value={stats?.latest ? `${formatTemp(stats.latest.temperature_celsius)}` : '—'}
          sub={stats?.latest ? formatTime(stats.latest.timestamp || stats.latest.created_at) : 'No data'}
          status={stats?.latest?.status}
        />
        <StatTile label="Min"     value={stats ? formatTemp(stats.min) : '—'} sub={stats ? `${stats.count} readings` : 'No data'} />
        <StatTile label="Max"     value={stats ? formatTemp(stats.max) : '—'} />
        <StatTile label="Average" value={stats ? formatTemp(stats.avg) : '—'} />
      </div>

      {/* Recent readings list */}
      <div className="mt-4" data-testid="temperature-readings-list">
        <p className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-500 mb-2">
          Recent readings
        </p>
        {loading ? (
          <div className="text-sm text-slate-500 p-3 border border-dashed border-slate-200 rounded-lg">
            Loading…
          </div>
        ) : readings.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 p-3 border border-dashed border-slate-200 rounded-lg">
            <Inbox className="w-4 h-4" />
            No temperature readings recorded for this batch yet.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {readings.slice(0, 20).map((r, i) => (
              <ReadingRow key={r.id || i} reading={r} />
            ))}
            {readings.length > 20 && (
              <p className="text-[11px] text-slate-500 px-3 py-2 bg-slate-50">
                Showing 20 of {readings.length} readings for this batch.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------------- Subcomponents ---------------- */
const SessionStatusBadge = ({ session }) => {
  if (session) {
    return (
      <span
        data-testid="temperature-session-badge"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-emerald-800 whitespace-nowrap"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Session active · {session.sensor_id || 'sensor'}
      </span>
    );
  }
  return (
    <span
      data-testid="temperature-session-badge"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      No active session
    </span>
  );
};

const StatTile = ({ label, value, sub, status }) => {
  const tone = statusTone(status);
  return (
    <div
      className={`rounded-lg border p-2.5 sm:p-3 ${tone.cardBg} ${tone.cardBorder}`}
      data-testid={`temperature-stat-${label.toLowerCase()}`}
    >
      <p className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p
        className={`text-lg sm:text-xl font-bold mt-0.5 tabular-nums ${tone.text}`}
        style={{ fontFamily: 'Manrope' }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 truncate">
          {sub}
        </p>
      )}
    </div>
  );
};

const ReadingRow = ({ reading }) => {
  const tone = statusTone(reading.status);
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <div className="flex items-center gap-2.5 min-w-0">
        <Snowflake className={`w-4 h-4 flex-shrink-0 ${tone.text}`} />
        <div className="min-w-0">
          <p className={`text-sm font-semibold tabular-nums ${tone.text}`}>
            {formatTemp(reading.temperature_celsius)}
          </p>
          <p className="text-[11px] text-slate-500 truncate">
            {formatTime(reading.timestamp || reading.created_at)}
            {reading.sensor_id ? ` · ${reading.sensor_id}` : ''}
          </p>
        </div>
      </div>
      <StatusChip status={reading.status} />
    </div>
  );
};

const StatusChip = ({ status }) => {
  const s = (status || '').toLowerCase();
  if (s === 'critical') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-[10px] sm:text-xs font-semibold text-rose-700">
        <AlertTriangle className="w-3 h-3" /> Critical
      </span>
    );
  }
  if (s === 'warning') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] sm:text-xs font-semibold text-amber-700">
        <AlertTriangle className="w-3 h-3" /> Warning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] sm:text-xs font-semibold text-emerald-700">
      <CheckCircle className="w-3 h-3" /> Normal
    </span>
  );
};

/* ---------------- Helpers ---------------- */
const statusTone = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'critical') {
    return {
      text: 'text-rose-700',
      cardBg: 'bg-rose-50/40',
      cardBorder: 'border-rose-200',
    };
  }
  if (s === 'warning') {
    return {
      text: 'text-amber-700',
      cardBg: 'bg-amber-50/40',
      cardBorder: 'border-amber-200',
    };
  }
  return {
    text: 'text-slate-800',
    cardBg: 'bg-slate-50',
    cardBorder: 'border-slate-200',
  };
};

const formatTemp = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(1)} °C`;
};

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
};

export default TemperatureMonitoringCard;
